import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { ContactCenterApiService } from '../../../v2/apis/contact-center/contact-center-api.service';

export interface AssignTicketDialogData {
    ticketId: number | string;
    currentAssignee?: string;
    referenceType?: string;
}

@Component({
    selector: 'app-assign-ticket-dialog',
    templateUrl: './assign-ticket-dialog.component.html',
    styleUrls: ['./assign-ticket-dialog.component.scss'],
    standalone: false
})
export class AssignTicketDialogComponent implements OnInit {
    searchControl = new FormControl('');
    userList: any[] = [];
    filteredUsers: any[] = [];
    selectedUsers: any[] = [];
    isLoading: boolean = false;
    isSubmitting: boolean = false;

    constructor(
        public dialogRef: MatDialogRef<AssignTicketDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: AssignTicketDialogData,
        private contactCenterService: ContactCenterApiService,
        private toastr: ToastrService
    ) { }

    ngOnInit(): void {
        this.loadUsers();
        this.searchControl.valueChanges.subscribe(value => {
            this.filterUsers(value || '');
        });
    }

    loadUsers(): void {
        this.isLoading = true;
        const payload = {
            reference_id: 0,
            reference_type: this.data.referenceType || 'feedback',
            assigned_to_category: '',
            assigned_by_wwid: Number(sessionStorage.getItem('userId')),
            comment: '',
            business_unit_code: sessionStorage.getItem('businessUnitCode') || '',
            region: ''
        };
        this.contactCenterService.createContactCenterAssignment(payload).subscribe({
            next: (response) => {
                this.userList = Array.isArray(response) ? response : (response?.data || response?.users || []);
                this.filteredUsers = [...this.userList];
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
            }
        });
    }

    filterUsers(searchTerm: string): void {
        if (!searchTerm) {
            this.filteredUsers = [...this.userList];
            return;
        }
        const term = searchTerm.toLowerCase();
        this.filteredUsers = this.userList.filter(user =>
            (user.name || user.first_name || user.given_name || '').toLowerCase().includes(term) ||
            (user.email || '').toLowerCase().includes(term) ||
            (user.wwid?.toString() || '').includes(term)
        );
    }

    isSelected(user: any): boolean {
        return this.selectedUsers.some(u => (u.wwid || u.id) === (user.wwid || user.id));
    }

    toggleUser(user: any): void {
        const index = this.selectedUsers.findIndex(u => (u.wwid || u.id) === (user.wwid || user.id));
        if (index > -1) {
            this.selectedUsers.splice(index, 1);
        } else {
            this.selectedUsers.push(user);
        }
    }

    removeUser(user: any): void {
        this.selectedUsers = this.selectedUsers.filter(u => (u.wwid || u.id) !== (user.wwid || user.id));
    }

    getUserDisplayName(user: any): string {
        return user.name || user.first_name || user.given_name || user.email || `User ${user.wwid || user.id}`;
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    onAssign(): void {
        if (this.selectedUsers.length === 0) return;

        this.isSubmitting = true;
        const assigneeNames = this.selectedUsers.map(u => this.getUserDisplayName(u)).join(', ');
        const payload = {
            reference_id: Number(this.data.ticketId),
            reference_type: this.data.referenceType || 'feedback',
            assigned_to_category: assigneeNames,
            assigned_by_wwid: Number(sessionStorage.getItem('userId')),
            comment: '',
            business_unit_code: sessionStorage.getItem('businessUnitCode') || '',
            region: ''
        };

        this.contactCenterService.createContactCenterAssignment(payload).subscribe({
            next: () => {
                this.toastr.success('Ticket assigned successfully.');
                this.dialogRef.close({ assignee: assigneeNames, selectedUsers: this.selectedUsers });
                this.isSubmitting = false;
            },
            error: () => {
                this.toastr.error('Failed to assign ticket.');
                this.isSubmitting = false;
            }
        });
    }
}
